/*
 * Whalerknits conversion and attribution repair — 2026-07-28.
 *
 * Responsibilities:
 * - Preserve paid-click identity through the session and onto the Shopify cart.
 * - Attach that identity to Klaviyo signup profiles and lead events.
 * - Emit one GA4 generate_lead event without sending email to Google.
 * - Keep the paid-landing popup from interrupting arrival while retaining its teaser.
 * - Require an explicit size choice on the main product page.
 * - Hide the Klaviyo teaser while the cart drawer is open.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'wk_attribution_v1';
  var FORM_ID = 'WTV4Pi';
  var ATTRIBUTION_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid'
  ];

  function clean(value) {
    return String(value || '').trim().slice(0, 255);
  }

  function readStoredAttribution() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  }

  function currentTouch() {
    var params = new URLSearchParams(window.location.search);
    var touch = {};

    ATTRIBUTION_KEYS.forEach(function (key) {
      var value = clean(params.get(key));
      if (value) touch[key] = value;
    });

    if (Object.keys(touch).length) {
      touch.landing_page = clean(window.location.pathname);
      touch.captured_at = new Date().toISOString();
    }

    return touch;
  }

  function captureAttribution() {
    var stored = readStoredAttribution();
    var touch = currentTouch();

    if (Object.keys(touch).length) {
      stored.first_touch = stored.first_touch || touch;
      stored.last_touch = touch;

      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } catch (error) {
        // Attribution still remains available in memory for this page.
      }
    }

    return stored;
  }

  var attribution = captureAttribution();

  function activeTouch() {
    return attribution.last_touch || attribution.first_touch || {};
  }

  function isPaidTouch(touch) {
    var medium = clean(touch.utm_medium).toLowerCase();
    return Boolean(
      touch.gclid ||
      touch.gbraid ||
      touch.wbraid ||
      touch.fbclid ||
      /(^|_)(cpc|ppc|paid|paid_social|display)(_|$)/.test(medium)
    );
  }

  function cartAttributes() {
    var first = attribution.first_touch || {};
    var last = attribution.last_touch || {};
    var attributes = {};

    ATTRIBUTION_KEYS.forEach(function (key) {
      if (first[key]) attributes['wk_first_' + key] = clean(first[key]);
      if (last[key]) attributes['wk_last_' + key] = clean(last[key]);
    });

    if (first.landing_page) attributes.wk_first_landing_page = clean(first.landing_page);
    if (last.landing_page) attributes.wk_last_landing_page = clean(last.landing_page);
    return attributes;
  }

  function persistCartAttribution() {
    var attributes = cartAttributes();
    if (!Object.keys(attributes).length) return;

    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({ attributes: attributes })
    }).catch(function () {
      // The session copy remains available for Klaviyo even if cart persistence fails.
    });
  }

  function klaviyoProfileProperties(email) {
    var first = attribution.first_touch || {};
    var last = attribution.last_touch || {};
    var properties = { email: clean(email) };

    ATTRIBUTION_KEYS.forEach(function (key) {
      if (first[key]) properties['WK First ' + key] = clean(first[key]);
      if (last[key]) properties['WK Last ' + key] = clean(last[key]);
    });

    if (first.landing_page) properties['WK First Landing Page'] = clean(first.landing_page);
    if (last.landing_page) properties['WK Last Landing Page'] = clean(last.landing_page);
    properties['WK Attribution Captured At'] = new Date().toISOString();
    return properties;
  }

  function eventProperties(formId, source) {
    var first = attribution.first_touch || {};
    var last = attribution.last_touch || {};
    return {
      form_id: clean(formId),
      capture_source: clean(source),
      first_utm_source: clean(first.utm_source),
      first_utm_medium: clean(first.utm_medium),
      first_utm_campaign: clean(first.utm_campaign),
      first_utm_content: clean(first.utm_content),
      last_utm_source: clean(last.utm_source),
      last_utm_medium: clean(last.utm_medium),
      last_utm_campaign: clean(last.utm_campaign),
      last_utm_content: clean(last.utm_content),
      landing_page: clean(last.landing_page || first.landing_page)
    };
  }

  function emitGaLead(properties) {
    var gaProperties = {
      lead_source: properties.capture_source,
      form_id: properties.form_id,
      campaign_id: properties.last_utm_campaign || properties.first_utm_campaign,
      campaign_content: properties.last_utm_content || properties.first_utm_content
    };

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'generate_lead', gaProperties);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'generate_lead' }, gaProperties));
    }
  }

  function identifyLead(email, formId, source) {
    email = clean(email);
    if (!email) return;

    var properties = eventProperties(formId, source);
    emitGaLead(properties);

    function sendToKlaviyo() {
      if (!window.klaviyo || typeof window.klaviyo.identify !== 'function') return false;

      window.klaviyo.identify(klaviyoProfileProperties(email), function () {
        if (window.klaviyo && typeof window.klaviyo.track === 'function') {
          window.klaviyo.track('WK Captured Lead', properties);
        }
      });
      return true;
    }

    if (!sendToKlaviyo()) {
      var attempts = 0;
      var timer = window.setInterval(function () {
        attempts += 1;
        if (sendToKlaviyo() || attempts >= 20) window.clearInterval(timer);
      }, 250);
    }
  }

  function suppressPaidLandingPopup() {
    if (!isPaidTouch(activeTouch())) return;

    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      document.body && document.body.classList.remove('klaviyo-prevent-body-scrolling');

      var form = document.querySelector('[data-testid="klaviyo-form-' + FORM_ID + '"]');
      var popup = form && form.closest('[data-testid="POPUP"]');
      var close = popup && popup.querySelector('.klaviyo-close-form');

      if (close) {
        close.dispatchEvent(new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        }));
        window.clearInterval(timer);
      } else if (attempts >= 20) {
        window.clearInterval(timer);
      }
    }, 50);
  }

  window.addEventListener('klaviyoForms', function (event) {
    if (!event.detail) return;

    if (event.detail.formId === FORM_ID && event.detail.type === 'open') {
      suppressPaidLandingPopup();
    }

    if (event.detail.type === 'submit') {
      identifyLead(
        event.detail.metaData && event.detail.metaData.email,
        event.detail.formId,
        'klaviyo_form'
      );
    }
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.wkLeadTracked === 'true') return;

    var email = form.querySelector('input[name="contact[email]"]');
    if (!email || !clean(email.value)) return;

    form.dataset.wkLeadTracked = 'true';
    identifyLead(email.value, form.id || 'shopify_newsletter', 'shopify_newsletter');
  }, true);

  function sizeFieldset(picker) {
    return Array.from(picker.querySelectorAll('fieldset')).find(function (fieldset) {
      var legend = fieldset.querySelector('legend');
      return legend && clean(legend.textContent).replace(/:$/, '').toLowerCase() === 'size';
    });
  }

  function removeSizeError(picker) {
    var error = picker.querySelector('.wk-size-required-error');
    if (error) error.remove();
    picker.removeAttribute('data-wk-size-missing');
  }

  function showSizeError(picker, fieldset) {
    removeSizeError(picker);

    var error = document.createElement('p');
    error.className = 'wk-size-required-error';
    error.setAttribute('role', 'alert');
    error.textContent = 'Please choose your size before adding to cart.';
    fieldset.appendChild(error);
    picker.setAttribute('data-wk-size-missing', 'true');

    var control = fieldset.querySelector('button, input:not([disabled])');
    if (control) control.focus({ preventScroll: false });
  }

  function initializeSizeGate(root) {
    var scope = root && root.querySelectorAll ? root : document;

    scope.querySelectorAll('variant-picker[context="main_product"]').forEach(function (picker) {
      if (picker.dataset.wkSizeGate === 'ready') return;

      var fieldset = sizeFieldset(picker);
      var formId = picker.getAttribute('form-id');
      var form = formId && document.getElementById(formId);
      if (!fieldset || !form) return;

      picker.dataset.wkSizeGate = 'ready';
      picker.dataset.wkSizeChosen = new URLSearchParams(window.location.search).has('variant')
        ? 'true'
        : 'false';
      form.closest('product-form') && form.closest('product-form').classList.toggle(
        'wk-size-choice-pending',
        picker.dataset.wkSizeChosen !== 'true'
      );

      fieldset.addEventListener('change', function () {
        picker.dataset.wkSizeChosen = 'true';
        form.closest('product-form') && form.closest('product-form').classList.remove(
          'wk-size-choice-pending'
        );
        removeSizeError(picker);
      });
    });
  }

  function currentPickerForForm(form) {
    var formId = form.getAttribute('id');
    var picker = Array.from(
      document.querySelectorAll('variant-picker[context="main_product"]')
    ).find(function (candidate) {
      return candidate.getAttribute('form-id') === formId;
    });
    return picker;
  }

  function blockUnchosenSize(event, form) {
    var picker = currentPickerForForm(form);
    if (!picker || picker.dataset.wkSizeChosen === 'true') return false;

    var fieldset = sizeFieldset(picker);
    if (!fieldset) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    showSizeError(picker, fieldset);
    return true;
  }

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;

    var trigger = event.target.closest(
      'product-form button[type="submit"], product-form .shopify-payment-button'
    );
    var productForm = trigger && trigger.closest('product-form');
    var form = productForm && productForm.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    blockUnchosenSize(event, form);
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    blockUnchosenSize(event, form);
  }, true);

  function synchronizeCartState() {
    var drawer = document.querySelector('#cart-drawer');
    document.body.classList.toggle('wk-cart-open', Boolean(drawer && drawer.hasAttribute('open')));
  }

  function initializeCartObserver() {
    var drawer = document.querySelector('#cart-drawer');
    if (!drawer || drawer.dataset.wkCartObserver === 'ready') return;

    drawer.dataset.wkCartObserver = 'ready';
    synchronizeCartState();
    new MutationObserver(synchronizeCartState).observe(drawer, {
      attributes: true,
      attributeFilter: ['open']
    });
  }

  if (isPaidTouch(activeTouch())) {
    document.documentElement.classList.add('wk-paid-landing');
  }

  function initialize() {
    persistCartAttribution();
    initializeSizeGate(document);
    initializeCartObserver();
    suppressPaidLandingPopup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeSizeGate(event.target);
    initializeCartObserver();
  });

  document.addEventListener('variant:change', function () {
    window.requestAnimationFrame(function () {
      initializeSizeGate(document);
    });
  });
})();
